import { z } from 'zod';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { FirebaseError } from 'firebase/app';
import { doc, getDoc } from 'firebase/firestore';
import { zodResolver } from '@hookform/resolvers/zod';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { captureException as sentryCaptureException } from '@sentry/react';
import { createUserWithEmailAndPassword, sendEmailVerification, signInWithEmailAndPassword } from 'firebase/auth';
import { Typography, Link, Button, TextField, InputAdornment, IconButton } from '@mui/material';

import { MINIMUM_DATA_LENGTH } from '@shared/utils/constants';

import { auth, createRecoveryProbeAuth, db } from '../../../firebase-config';
import createPatientData from '../../../functions/create-patient-data';
import { PasswordStrengthIndicator } from '../components';
import { AuthenticationWrapper, classes } from '../AuthenticationWrapper';
import { useAnalytics, useMetaPixel, useMicrosoftUET } from '../../../hooks';

const MIN_PASSWORD_LENGTH = 8;
const CREATION_TO_LAST_SIGN_IN_WINDOW_IN_MS = 2 * 60 * 1000;

const REGISTER_RECOVERABLE_AUTH_ERROR_CODES = new Set([
  'auth/internal-error',
  'auth/network-request-failed',
  'auth/timeout',
]);

const isAppCheckTokenError = (error) => {
  if (!(error instanceof FirebaseError)) {
    return false;
  }

  const normalizedError = `${error.code} ${error.message || ''}`.toLowerCase();

  return (
    normalizedError.includes('auth/firebase-app-check-token-is-invalid') ||
    normalizedError.includes('app-check-token-is-invalid')
  );
};

const shouldAttemptCredentialRecovery = (error) => {
  if (!(error instanceof FirebaseError)) {
    return false;
  }

  if (isAppCheckTokenError(error)) {
    return false;
  }

  if (REGISTER_RECOVERABLE_AUTH_ERROR_CODES.has(error.code)) {
    return true;
  }

  return /timeout/i.test(error.message || '');
};

/** @param {import('firebase/auth').User | null | undefined} user */
const isNewlyCreatedAuthUser = (user) => {
  const creationTimeMs = Date.parse(user?.metadata?.creationTime || '');
  const lastSignInTimeMs = Date.parse(user?.metadata?.lastSignInTime || '');

  if (Number.isNaN(creationTimeMs) || Number.isNaN(lastSignInTimeMs)) {
    return false;
  }

  const creationToLastSignInDeltaMs = lastSignInTimeMs - creationTimeMs;

  if (creationToLastSignInDeltaMs < 0) {
    return false;
  }

  return creationToLastSignInDeltaMs <= CREATION_TO_LAST_SIGN_IN_WINDOW_IN_MS;
};

const registerSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z
    .string()
    .min(MIN_PASSWORD_LENGTH, `Password must be at least ${MIN_PASSWORD_LENGTH} characters`)
    .refine((val) => val === val.trim(), {
      message: 'Password cannot start or end with spaces',
    }),
});

/**
 * @typedef {z.infer<typeof registerSchema>} FormType
 */

/**
 * Handles the error logic for the submission of the register form
 * @param {Object} params
 * @param {Error} params.error
 * @param {import('react-hook-form').UseFormSetError<FormType>} params.setError
 * @param {string} params.email
 */
const handleOnSubmitErrors = ({ error, setError, email }) => {
  if (!(error instanceof FirebaseError)) {
    const sentryIssueId = sentryCaptureException(error, {
      level: 'error',
      tags: { email, domain: 'auth_email', issueIn: 'RegisterPage:handleOnSubmitError' },
      extra: { errorType: 'unknown_register_error' },
    });

    setError('root', {
      type: 'sentry',
      message: `An unexpected error occurred. Try again later. Id: ${sentryIssueId}`,
    });

    return;
  }

  switch (error.code) {
    case 'auth/invalid-email':
      setError('email', { message: 'This email is not valid' });
      break;
    case 'auth/email-already-in-use':
      setError('email', { message: 'This email is already in use' });
      break;
    case 'auth/weak-password':
      setError('password', { message: 'Please enter a stronger password' });
      break;
    case 'auth/operation-not-allowed':
      setError('root', {
        message: 'This account has been disabled. Please contact support@candor.org.',
      });
      break;
    default: {
      const sentryIssueId = sentryCaptureException(error, {
        level: 'error',
        tags: { email, domain: 'auth_email', issueIn: 'RegisterPage:handleOnSubmitError' },
        extra: { errorType: 'firebase_register_error' },
      });

      setError('root', {
        type: 'sentry',
        message: `An unexpected error occurred. Please contact support@candor.org with ${error.code}. Id: ${sentryIssueId}`,
      });
    }
  }
};

const ensurePatientDataIfBroken = async ({ userId, userEmail, formName, formInputs, index }) => {
  const patientRef = doc(db, 'patients', userId);
  const patientSnap = await getDoc(patientRef);
  const patientData = patientSnap.data() || {};

  const shouldRepair = !patientSnap.exists() || Object.keys(patientData).length < MINIMUM_DATA_LENGTH;

  if (!shouldRepair) return;

  const { error } = await createPatientData({ userId, userEmail, formName, formInputs, index });

  if (error) throw new Error(error);
};

const recoverCredentialFromExistingAuth = async ({ email, password, error }) => {
  if (!shouldAttemptCredentialRecovery(error)) {
    return null;
  }

  let disposeRecoveryProbe = async () => {};
  let recoveryProbeAuth = null;

  try {
    const recoveryProbe = await createRecoveryProbeAuth({ prefix: 'register-recovery' });
    recoveryProbeAuth = recoveryProbe.probeAuth;
    disposeRecoveryProbe = recoveryProbe.dispose;

    const recoveredCredentialProbe = await signInWithEmailAndPassword(recoveryProbeAuth, email, password);

    if (isNewlyCreatedAuthUser(recoveredCredentialProbe.user)) {
      return signInWithEmailAndPassword(auth, email, password);
    }

    return null;
  } catch (err) {
    (import.meta.env.DEV ? console.error : sentryCaptureException)(err, {
      level: 'error',
      extra: { issueIn: 'RegisterPage:recoverCredentialFromExistingAuth' },
    });

    return null;
  } finally {
    await disposeRecoveryProbe().catch((cleanupError) => {
      (import.meta.env.DEV ? console.error : sentryCaptureException)(cleanupError, {
        level: 'error',
        extra: { issueIn: 'RegisterPage:recoverCredentialFromExistingAuth:cleanup' },
      });
    });
  }
};

/**
 * @typedef {{
 * } & ({
 * formContext?: false;
 * } | {
 * formContext: true;
 * formName: FormCategory
 * index: number;
 * formInputs: Object;
 * }) } RegisterPageProps
 */

/**
 * RegisterPage Component - Handles new user registration
 * @param {RegisterPageProps} props - Component props
 */
export const RegisterPage = (props) => {
  useMicrosoftUET();
  useMetaPixel('register');
  const { formContext } = props;
  const analytics = useAnalytics();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = React.useState(false);

  const form = useForm({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const {
    register,
    setError,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = form;

  /**
   * Handles the submission of the registration form
   * @param {FormType} data - The data from the registration form
   */
  const onSubmit = async (data) => {
    const cleanseEmail = data.email.trim().toLowerCase();
    const p = props;

    /** @type {import('firebase/auth').UserCredential | null} */
    let credential = null;

    try {
      credential = await createUserWithEmailAndPassword(auth, cleanseEmail, data.password);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.log('Error during createUserWithEmailAndPassword:', error);
      }

      credential = await recoverCredentialFromExistingAuth({ error, email: cleanseEmail, password: data.password });

      if (!credential) {
        handleOnSubmitErrors({ error, setError, email: cleanseEmail });
        return;
      }
    }

    const userId = credential?.user?.uid;
    try {
      if (!userId) throw new Error('Unable to resolve authenticated user while creating account');

      const healthCategory = p.formContext ? p.formName || undefined : undefined;

      await ensurePatientDataIfBroken({
        userId,
        userEmail: cleanseEmail,
        ...(p.formContext && { formName: p.formName, formInputs: p.formInputs, index: p.index }),
      });

      if (!credential.user.emailVerified) {
        await sendEmailVerification(credential.user);
      }
      analytics.capture('register_flow:sign_up_success', {
        registerType: p.formContext ? 'form' : 'page',
        userId,
        userEmail: cleanseEmail,
        ...(formContext && {
          healthCategory,
          $groups: { healthCategory },
        }),
      });
    } catch (error) {
      handleOnSubmitErrors({ error, setError, email: cleanseEmail });
    }
  };

  return (
    <AuthenticationWrapper>
      <AuthenticationWrapper.Container id="register-page">
        <AuthenticationWrapper.Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
          <AuthenticationWrapper.Header>
            <AuthenticationWrapper.Title>Create Account</AuthenticationWrapper.Title>
          </AuthenticationWrapper.Header>
          <TextField
            size="small"
            label="Email"
            variant="outlined"
            autoComplete="username"
            className={classes.input}
            {...register('email')}
            error={!!errors.email}
            helperText={errors.email?.message}
          />
          <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '0.5rem' }}>
            <TextField
              size="small"
              type={showPassword ? 'text' : 'password'}
              label="Password"
              autoComplete="new-password"
              variant="outlined"
              className={classes.input}
              {...register('password')}
              error={!!errors.password}
              helperText={errors.password?.message}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((prev) => !prev)}>
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <PasswordStrengthIndicator />
          </div>

          {errors.root?.message && (
            <Typography variant="body2" color="error" align="center">
              {errors.root?.message}
            </Typography>
          )}

          <AuthenticationWrapper.Actions>
            <Button
              type="submit"
              className={classes.button}
              loading={isSubmitting}
              disabled={isSubmitSuccessful}
              loadingPosition="end"
              variant="contained"
              color="primary"
            >
              {isSubmitting ? 'Signing up' : 'Sign up'}
            </Button>

            <Typography variant="caption" align="center" display="block">
              By signing up, you agree to the Candor Medical{' '}
              <Link underline="none" target="_blank" href="https://www.candormedical.com/terms-and-conditions">
                Terms and Conditions
              </Link>
              , including receiving marketing communications from us. You can unsubscribe at any time.
            </Typography>
            <Typography variant="caption" align="center" display="block">
              Please see our{' '}
              <Link underline="none" target="_blank" href="https://www.candormedical.com/privacy-collection-notice">
                Privacy Collection Notice
              </Link>{' '}
              and{' '}
              <Link underline="none" target="_blank" href="https://www.candormedical.com/privacy-policy">
                Privacy Policy
              </Link>{' '}
              for information about how we collect, use and protect your personal information.
            </Typography>
          </AuthenticationWrapper.Actions>
        </AuthenticationWrapper.Form>
        {!formContext && (
          <Button variant="text" onClick={() => navigate('/login')} className={classes.link}>
            I already have an account
          </Button>
        )}
      </AuthenticationWrapper.Container>
    </AuthenticationWrapper>
  );
};
